// 150 §3 — THE PERIOD BOUNDARIES, ONE COPY.
//
// The ruled options: current work week · current month from the 1st · current
// quarter from 1 January · current year from 1 January · free from/to. The
// arithmetic the brief pointed at (`ReportsView.tsx:500-541`) exists and was
// MEASURED before being lifted, and the measurement disagrees with lifting it
// verbatim:
//
//   `new Date(y, m, 1).toISOString().slice(0, 10)` converts a LOCAL midnight
//   to UTC before printing the date, so for any positive offset it prints the
//   PREVIOUS day — all day long, not just around midnight. In Brussels
//   (UTC+1/+2) ReportsView's August window reads [Jul 31 .. Aug 30]: both
//   edges wrong, 24 hours a day. (Its `todayStr`-style stamps shift only in
//   the small hours; the midnight-anchored boundaries shift always.)
//
// So this module prints LOCAL calendar dates and never round-trips through
// UTC.
//
// 174 §3d — THE SENTENCE THAT STOOD HERE OUTLIVED ITS REPAIR. It said
// "ReportsView still carries its own copy with the defect; repointing it here
// is a separate, labelled repair commit". 150 §3 itself already repointed
// `monthBounds`/`quarterBounds`/`yearBounds` there (ReportsView.tsx's own
// comment says so), and 174 §3d finished the last of it — the
// By-Collaborator window's `thisMonth + "-01"`, a `toISOString()` slice
// paired with a LOCAL date, which disagreed with its partner between 00:00
// and 02:00 local. Brief 174 §3.3 read this note as current and asked for a
// repair that was months done; a note that schedules work must be deleted by
// the work, or it schedules it twice.
//
// THE WORK WEEK IS THE FIRM'S. `FirmSettings.scheduleAxis` says which days the
// firm works; `resolveScheduleAxis` already owns the fallback (Mon–Fri when
// the setting or any of its keys is absent), so the SCHED_* constants are
// reached through it and never read here. The window is the FIRST working day
// of the current Monday-first week through the LAST — a firm that works
// Saturday gets Mon..Sat; rows logged on a non-working weekend fall outside
// "current work week", which is what the firm's own setting says a work week
// is. An axis with no working day at all falls back to Mon..Sun rather than
// an empty window.
import { resolveScheduleAxis } from "./scheduleAxis";
import type { ScheduleAxis } from "./scheduleAxis";
import { resolveAccountingYearStart } from "./accountingYear.mjs";

// ── 174 §3 [RULED 2026-09-08, Laurent] — THE NINE, EVERYWHERE BILLING-RELATED
//
// "Billing runs monthly, quarterly and yearly, so every billing-related period
// picker offers the same nine": this week (Mon–Sun) · last week · this month ·
// last month · this trimester · previous trimester · this year · previous year
// · bespoke. Four of them existed nowhere in the tree.
//
// EVERY BOUNDARY LIVES HERE AND NOWHERE ELSE (§3.4). The four "previous"
// windows are an OFFSET on the four functions that already computed the
// current one, not four new functions: a second copy of "previous quarter" is
// the defect this section closes, and an offset cannot disagree with the
// present window it is one step behind. `now` keeps its position as the first
// parameter so no existing call site changes.
export type PeriodKind =
  | ""
  /* 194 B.1d — the ten-day window the e-mail rows open on: today and the nine
     days before it, a rolling window rather than a calendar one. Offered
     wherever the list below is, because one list is the rule (174 §3.4). */
  | "last10days"
  | "week"
  | "lastweek"
  /* 174 §3b — the FIRM's work week (FirmSettings.scheduleAxis, 150 §3). No
     picker offers it; it is here so the answer 150 §3 ruled stays reachable
     through the same door now that "week" means the calendar week. */
  | "workweek"
  | "month"
  | "lastmonth"
  /* 213B §4 [RULED 40, Laurent 28/09] — THE FOUR QUARTERS OF THE CURRENT YEAR,
     « Q1 »… « Q4 » (« T1 »… · « K1 »…, the app's own word for a quarter), in
     place of « This quarter » and « Previous quarter ». A quarter not yet
     begun still offers its window — it may hold events. */
  | "q1"
  | "q2"
  | "q3"
  | "q4"
  /* 213B §4 — NO PICKER OFFERS THESE TWO since the four quarters took their
     place; they stay bounded so a value a host kept can still open a window
     (the census found none kept anywhere — no storage, preference or link):
     `quarter` opens the current quarter's; `lastquarter` the previous one's
     when it lies in the same year, else nothing (every date). */
  | "quarter"
  | "lastquarter"
  | "year"
  | "lastyear"
  /* 198 §C/§D [RULED 2026-09-19, Laurent] — the firm's ACCOUNTING YEAR:
     « Exercice comptable en cours » is the FULL current exercise (from the
     last start on or before today to the day before the next — not a
     year-to-date), « précédent » the twelve months before it. They read the
     firm's start through the axis seam (D.3), so every picker that offers
     the list moves together when Paramètres changes the start. */
  | "fiscalyear"
  | "lastfiscalyear"
  | "free";
export type PeriodValue = { kind: PeriodKind; from?: string; to?: string };

/** One option a period picker offers: the kind, and the words for it. No `t`
 *  crosses this boundary — each host renders the key with its own translator,
 *  the way `emailRowWeight` hands its labels over. */
export interface PeriodOption {
  kind: PeriodKind;
  key: string;
  fallback: string;
  /** 213B §4 — the key's placeholders, where it has one (the quarters' `n` in
   *  `proforma.period.quarter_short`, « T{n} »); a host spreads them into its
   *  translator's call beside the fallback. */
  vars?: Readonly<Record<string, number>>;
}

/**
 * THE NINE, IN LAURENT'S OWN ORDER, so four pickers cannot offer four orders —
 * and, since 194 B.1d, a TENTH at the head: the last ten days, the shortest
 * window and the one the e-mail search rows open on.
 *
 * "All dates" is NOT here: it is not a period, it is the absence of one, and
 * only the surfaces that already had it keep it (the time-entry filter, and
 * 194 §D's messages pane). An email search with no window is an undated
 * `$search` under Graph's 1,000-row ceiling (194 A.0).
 */
export const PERIOD_OPTIONS: readonly PeriodOption[] = [
  { kind: "last10days", key: "period.last_10_days", fallback: "Last 10 days" },
  { kind: "week", key: "period.this_week", fallback: "This week" },
  { kind: "lastweek", key: "period.last_week", fallback: "Last week" },
  { kind: "month", key: "period.this_month", fallback: "This month" },
  { kind: "lastmonth", key: "period.last_month", fallback: "Last month" },
  // 213B §4 [RULED 40] — the four quarters of the current year, in the pair's
  // place and order, each through the app's one word for a quarter
  // (`proforma.period.quarter_short`: « Q{n} » · « T{n} » · « K{n} », 207 §11).
  { kind: "q1", key: "proforma.period.quarter_short", vars: { n: 1 }, fallback: "Q1" },
  { kind: "q2", key: "proforma.period.quarter_short", vars: { n: 2 }, fallback: "Q2" },
  { kind: "q3", key: "proforma.period.quarter_short", vars: { n: 3 }, fallback: "Q3" },
  { kind: "q4", key: "proforma.period.quarter_short", vars: { n: 4 }, fallback: "Q4" },
  { kind: "year", key: "period.this_year", fallback: "This year" },
  { kind: "lastyear", key: "period.last_year", fallback: "Previous year" },
  // 198 §C.0 — the two exercises join the ONE list, after the calendar year's
  // pair and before « Libre »: every consumer of this list offers them at once.
  { kind: "fiscalyear", key: "period.this_fiscal_year", fallback: "Current accounting year" },
  { kind: "lastfiscalyear", key: "period.last_fiscal_year", fallback: "Previous accounting year" },
  { kind: "free", key: "period.free", fallback: "Free from/to…" },
];

/**
 * The "all dates" head, for the pickers that already offered one — as a LIST so
 * a caller spreads it or does not, and never has to build the head itself.
 *
 * A list rather than a lone object for a second reason worth stating:
 * `i18n-total`'s `prose-map` detector arms over a variable whose initializer is
 * an OBJECT LITERAL carrying words, which is exactly what a lone
 * `PERIOD_ALL_OPTION` is. Both offers are the same kind of thing — a keyed
 * option list whose `fallback` is the English a call site would otherwise have
 * written inline — so they take the same shape and neither needs an exemption.
 */
export const PERIOD_ALL_OPTIONS: readonly PeriodOption[] = [
  { kind: "", key: "period.all", fallback: "All dates" },
];

/** A LOCAL calendar date as "YYYY-MM-DD" — see the header for why this is
 *  never `toISOString().slice(0, 10)`. */
export const localYMD = (d: Date): string =>
  d.getFullYear() +
  "-" +
  String(d.getMonth() + 1).padStart(2, "0") +
  "-" +
  String(d.getDate()).padStart(2, "0");

export function workWeekBounds(axis: ScheduleAxis, now = new Date()): [string, string] {
  // Monday-first index of today; the Date is built from Y/M/D so time-of-day
  // never participates.
  const idx = (now.getDay() + 6) % 7;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - idx);
  const working = axis.days.map((d) => d.working);
  const first = working.indexOf(true);
  const last = working.lastIndexOf(true);
  const a = first === -1 ? 0 : first;
  const b = last === -1 ? 6 : last;
  const from = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + a);
  const to = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + b);
  return [localYMD(from), localYMD(to)];
}

/**
 * 174 §3 — THE CALENDAR WEEK, Mon–Sun, BESIDE the firm's work week above.
 *
 * Not a replacement: `workWeekBounds` is still the correct answer to "what does
 * this firm consider a working week" (150 §3, and deleting it would undo that
 * ruling). This is the answer to "the week", which is what a BILLING period
 * picker means — a Saturday time entry is billable whether or not the firm's
 * axis calls Saturday a working day.
 *
 * `offset` is in whole weeks: 0 this week, -1 last week. Built from Y/M/D so
 * time-of-day never participates, and printed as LOCAL dates (see the header).
 */
export function calendarWeekBounds(now = new Date(), offset = 0): [string, string] {
  const idx = (now.getDay() + 6) % 7; // Monday-first index of today
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - idx + offset * 7);
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
  return [localYMD(monday), localYMD(sunday)];
}

/** `offset` in whole months: 0 this month, -1 last month. `new Date` normalises
 *  a month of -1 or 12 into the neighbouring year, so December and January need
 *  no special case. */
export function monthBounds(now = new Date(), offset = 0): [string, string] {
  const m = now.getMonth() + offset;
  return [
    localYMD(new Date(now.getFullYear(), m, 1)),
    localYMD(new Date(now.getFullYear(), m + 1, 0)),
  ];
}

/** `offset` in whole quarters: 0 this trimester, -1 the previous one — which in
 *  Q1 is the previous YEAR's Q4, and falls out of the same normalisation. */
export function quarterBounds(now = new Date(), offset = 0): [string, string] {
  const q = Math.floor(now.getMonth() / 3) + offset;
  return [
    localYMD(new Date(now.getFullYear(), q * 3, 1)),
    localYMD(new Date(now.getFullYear(), q * 3 + 3, 0)),
  ];
}

/** 213B §4 [RULED 40] — quarter `n` (1 to 4) of `now`'s CALENDAR YEAR: 1 January
 *  to 31 March, and so on, as LOCAL dates (the module's own rule, above). The
 *  year is `now`'s, so in January last year's Q4 is reached through « Previous
 *  year » or « Free », not here. */
export function quarterOfYearBounds(n: number, now = new Date()): [string, string] {
  const q = Math.min(4, Math.max(1, Math.trunc(n))) - 1;
  return [
    localYMD(new Date(now.getFullYear(), q * 3, 1)),
    localYMD(new Date(now.getFullYear(), q * 3 + 3, 0)),
  ];
}
/** The quarter `now` falls in, 1 to 4. */
export const quarterOf = (now = new Date()): number => Math.floor(now.getMonth() / 3) + 1;
/** 213B §4 — the quarter kinds' windows: q1–q4 of the current year; and the two
 *  kinds no picker offers any more, bounded for a value a host kept [ADVISOR]:
 *  `quarter` opens the current quarter's window, `lastquarter` the previous
 *  one's when it lies in the same year — in Q1 it answers no window (every
 *  date; the host's own « all »), since the previous quarter is last year's,
 *  which « Previous year » or « Free » reach. `undefined` for any other kind. */
function quarterKindBounds(kind: PeriodKind, now: Date): [string, string] | null | undefined {
  if (kind === "q1" || kind === "q2" || kind === "q3" || kind === "q4") return quarterOfYearBounds(Number(kind.slice(1)), now);
  if (kind === "quarter") return quarterOfYearBounds(quarterOf(now), now);
  if (kind === "lastquarter") return quarterOf(now) > 1 ? quarterOfYearBounds(quarterOf(now) - 1, now) : null;
  return undefined;
}

/** `offset` in whole years: 0 this year, -1 the previous one. */
export function yearBounds(now = new Date(), offset = 0): [string, string] {
  const y = now.getFullYear() + offset;
  return [y + "-01-01", y + "-12-31"];
}

/**
 * 198 §D.2 [RULED — Laurent, 2026-09-19: "full current exercise (but of course
 * if it is the current one it will be incomplete)"] — THE FIRM'S ACCOUNTING
 * YEAR, ONE DEFINITION beside `yearBounds`, read by every consumer of the list.
 *
 * `start` is the firm's (day, month) from `resolveAccountingYearStart`
 * (src/lib/accountingYear.mjs — 1 January unless the firm set it). The current
 * exercise runs from the LAST start on or before today to the DAY BEFORE the
 * next start — the whole exercise, past today included; `offset` −1 is the
 * twelve months before it. Built from Y/M/D so time-of-day never participates,
 * printed as LOCAL dates (the header). `new Date(y, m, d)` normalises a start
 * such as 31 May + 1 year correctly, and the day before a start is
 * `new Date(y, m, d − 1)` for the same reason.
 *
 * With the default start this is exactly `yearBounds`: 1 January to 31
 * December. D.2a, THE CONSEQUENCE, STATED: a full exercise runs past today, so
 * under 197B-bis's coverage rule (emailReach.ts `narrowSpan` clips a span at
 * today) « Exercice comptable en cours » on « Limiter à » is covered by a
 * search whenever its START is inside the fetched window — correct, and not a
 * bug.
 */
export function accountingYearBounds(
  start: { month: number; day: number },
  now = new Date(),
  offset = 0,
): [string, string] {
  const y = now.getFullYear();
  const thisYearStart = new Date(y, start.month - 1, start.day);
  // the exercise that holds today: its start is this year's if that is not
  // after today, else last year's
  const today = new Date(y, now.getMonth(), now.getDate());
  const startYear = (thisYearStart <= today ? y : y - 1) + offset;
  const from = new Date(startYear, start.month - 1, start.day);
  const to = new Date(startYear + 1, start.month - 1, start.day - 1);
  return [localYMD(from), localYMD(to)];
}

/** 194 B.1d — the last `days` calendar days INCLUDING today: `[today − (days
 *  − 1), today]`, ten days for the e-mail rows' default. Built from Y/M/D so
 *  time-of-day never participates, and printed as LOCAL dates (the header). */
export function lastDaysBounds(days: number, now = new Date()): [string, string] {
  const n = Math.max(1, Math.floor(days));
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (n - 1));
  return [localYMD(from), localYMD(now)];
}

/** The chosen period as an inclusive [from, to] pair of date strings, or null
 *  when nothing narrows (no kind chosen; a free range with both edges empty).
 *  A free range may leave ONE edge empty — that edge is unbounded (null).
 *  `axisInput` is whatever the host holds: the resolved axis, the firm
 *  settings object, or nothing — `resolveScheduleAxis` accepts all three. */
export function periodBounds(
  p: PeriodValue | null | undefined,
  axisInput: unknown,
  now = new Date(),
): [string | null, string | null] | null {
  if (!p || !p.kind) return null;
  if (p.kind === "free") {
    const from = p.from || null;
    const to = p.to || null;
    return from || to ? [from, to] : null;
  }
  /* 174 §3b [RULED 2026-09-08, Laurent] — "THIS WEEK" IS THE CALENDAR WEEK
     HERE, AND THAT IS A BEHAVIOUR CHANGE. Laurent's nine say Mon–Sun. Until
     this commit `week` resolved to `workWeekBounds` — the FIRM's work week from
     `FirmSettings.scheduleAxis` (150 §3) — so on the default Mon–Fri axis a
     SATURDAY time entry fell OUTSIDE "current work week" and now falls inside
     it. Nothing else about the option changes, and the label moves with it
     ("Current work week" → "This week", a new key: the seed only inserts).

     `workWeekBounds` STAYS EXPORTED and is not deleted: it is still the correct
     answer to "what does this firm consider a working week", 150 §3 ruled it,
     and a surface that asks that question should reach for it rather than
     rebuild it. It has no caller today, which is stated rather than hidden —
     removing it would undo a ruling to satisfy a dead-code count.

     It stays reachable THROUGH THIS DOOR rather than only as an export: the
     kind `"workweek"` resolves to it, `axisInput` still feeds it, and neither
     the function nor the parameter becomes dead. No picker offers that kind —
     the nine do not include it — so nothing on screen changes twice. */
  const axis = resolveScheduleAxis(axisInput as Parameters<typeof resolveScheduleAxis>[0]);
  if (p.kind === "last10days") return lastDaysBounds(10, now);
  if (p.kind === "workweek") return workWeekBounds(axis, now);
  if (p.kind === "week") return calendarWeekBounds(now, 0);
  if (p.kind === "lastweek") return calendarWeekBounds(now, -1);
  if (p.kind === "month") return monthBounds(now);
  if (p.kind === "lastmonth") return monthBounds(now, -1);
  // 213B §4 [RULED 40] — the four quarters of the current year, and the two kept kinds (quarterKindBounds, above)
  const quarterWindow = quarterKindBounds(p.kind, now);
  if (quarterWindow !== undefined) return quarterWindow;
  if (p.kind === "lastyear") return yearBounds(now, -1);
  /* 198 §D.3 — THE SHAPE CHOSEN: the axis seam carries the firm's start
     WITHOUT widening the signature. `axisInput` is already "whatever the
     host holds — the resolved axis, the firm settings object, or nothing";
     the two exercise kinds read the settings object's
     `accountingYearStartMonth` / `accountingYearStartDay` off that same
     input (a resolved axis or null answers 1 January). So a host that wants
     the exercises to follow Paramètres passes the SETTINGS OBJECT here — as
     InvoiceView already did for the work week — and `narrowSpan` passes what
     the drawer holds (C.2). One parameter, one value, no third door. */
  if (p.kind === "fiscalyear") return accountingYearBounds(resolveAccountingYearStart(axisInput), now, 0);
  if (p.kind === "lastfiscalyear") return accountingYearBounds(resolveAccountingYearStart(axisInput), now, -1);
  return yearBounds(now);
}
